import type { ApiError } from './types.js';

/** Carries the server's `key` so a caller can branch without matching on prose. */
export class RequestFailed extends Error {
  constructor(
    readonly status: number,
    readonly key: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

/** The words of an unknown thrown value, for the one line a screen has to show it. */
export function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

let onUnauthorized: (() => void) | undefined;
let onForbidden: (() => void) | undefined;

/**
 * A 401 anywhere — a query, a publish, a tail — means there is no session left. It is reported
 * here, once, and `main.tsx` turns it into a redirect to the central login.
 *
 * A 403 with `ROLE_REQUIRED` is a different answer and must not become a redirect: that person
 * IS signed in, so the login would send them straight back and the two would bounce forever.
 * It clears the cached identity instead, which puts the "you need the engineer role" screen up.
 */
export function setAuthFailureHandlers(handlers: { unauthorized: () => void; forbidden: () => void }): void {
  onUnauthorized = handlers.unauthorized;
  onForbidden = handlers.forbidden;
}

/** The error for a non-2xx response, from the server's own body where it sent one. */
export async function failedRequest(response: Response): Promise<RequestFailed> {
  const body = (await response.json().catch(() => ({}))) as ApiError;
  if (response.status === 401) onUnauthorized?.();
  if (response.status === 403 && body.key === 'ROLE_REQUIRED') onForbidden?.();
  return new RequestFailed(
    response.status,
    body.key,
    body.message ?? `${response.status} ${response.statusText}`,
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
    // The session cookie is httpOnly and set by the auth service for the parent domain, so it
    // is sent with a same-origin request here without this code ever seeing a token.
    credentials: 'same-origin',
  });

  if (!response.ok) throw await failedRequest(response);

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
};
